<template>
  <section
    class="country-hero"
    :style="heroStyle"
  >
    <img
      v-if="hero.image"
      class="country-hero__image"
      :src="hero.image"
      :alt="hero.landmark"
    />
    <div
      v-else
      class="country-hero__fallback"
      aria-hidden="true"
    >
      <span class="country-hero__code">
        {{ countryCode.toUpperCase() }}
      </span>
    </div>
    <div class="country-hero__overlay"></div>

    <div class="country-hero__content">
      <img
        class="country-hero__flag"
        :src="getFlag(countryCode)"
        :alt="`${countryName} flag`"
      />

      <div class="country-hero__copy">
        <p class="country-hero__eyebrow">Your travel eSIM</p>
        <h1>eSIM for {{ countryName }}</h1>
        <p class="country-hero__description">
          Choose your data plan now and connect as soon as you arrive.
        </p>
        <div class="country-hero__location">
          <MapPin :size="16" />
          {{ hero.landmark }}
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { MapPin } from 'lucide-vue-next'
import { getFlag } from '~/utility/helper'
import { getCountryHero } from '~/utility/countryHero'

const props = defineProps<{
  countryCode: string
  countryName: string
}>()

const hero = computed(() => getCountryHero(props.countryCode))
const heroStyle = computed(() => ({ '--hero-accent': hero.value.accent }))
</script>

<style scoped>
.country-hero {
  position: relative;
  isolation: isolate;
  width: 100%;
  min-height: 390px;
  overflow: hidden;
  border-radius: 0;
  clip-path: polygon(
    0 0,
    100% 0,
    100% 100%,
    76% 86%,
    24% 86%,
    0 100%
  );
  color: #fff;
  background: linear-gradient(120deg, var(--hero-accent), #111827);
}

.country-hero__image,
.country-hero__fallback,
.country-hero__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.country-hero__image {
  object-fit: cover;
}

.country-hero__fallback {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 8%;
  box-sizing: border-box;
  background: radial-gradient(circle at 82% 42%, rgba(255,255,255,.24), transparent 30%);
}

.country-hero__code {
  font-size: clamp(5rem, 16vw, 11rem);
  font-weight: 900;
  letter-spacing: -.08em;
  opacity: .14;
}

.country-hero__overlay {
  background:
    linear-gradient(90deg, rgba(9,15,28,.9), rgba(9,15,28,.58) 56%, rgba(9,15,28,.24)),
    linear-gradient(0deg, rgba(9,15,28,.5), transparent 55%);
}

.country-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  max-width: 760px;
  padding: 3.25rem 3.25rem 9rem;
}

.country-hero__flag {
  width: 88px;
  height: 88px;
  flex: 0 0 88px;
  border: 4px solid #fff;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 12px 28px rgba(0,0,0,.28);
}

.country-hero__eyebrow {
  margin: 0 0 .35rem;
  font-size: .76rem;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
  opacity: .82;
}

.country-hero__copy h1 {
  margin: 0;
  color: inherit;
  font-size: clamp(2.15rem, 5vw, 3.65rem);
  line-height: 1.05;
  letter-spacing: -.035em;
}

.country-hero__description {
  max-width: 520px;
  margin: .8rem 0 1.1rem;
  font-size: 1rem;
  line-height: 1.55;
  opacity: .92;
}

.country-hero__location {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .55rem .8rem;
  border: 1px solid rgba(255,255,255,.35);
  border-radius: 999px;
  background: rgba(255,255,255,.14);
  font-size: .82rem;
  font-weight: 700;
  backdrop-filter: blur(8px);
}

@media (max-width: 600px) {
  .country-hero {
    min-height: 350px;
    border-radius: 0;
    clip-path: polygon(
      0 0,
      100% 0,
      100% 100%,
      82% 88%,
      18% 88%,
      0 100%
    );
  }

  .country-hero__content {
    gap: .9rem;
    padding: 2rem 1.25rem 7rem;
  }

  .country-hero__flag {
    width: 64px;
    height: 64px;
    flex-basis: 64px;
    border-width: 3px;
  }

  .country-hero__copy h1 {
    font-size: clamp(1.75rem, 9vw, 2.5rem);
  }

  .country-hero__description {
    font-size: .9rem;
  }
}
</style>
